import { t, tn, useI18n } from '../../i18n';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layer, Source } from '@vis.gl/react-maplibre';
import type { MapRef, MarkerDragEvent } from '@vis.gl/react-maplibre';
import MapLibreMap from '../../components/maps/MapLibreMap';
import MapPinMarker from '../../components/maps/MapPinMarker';
import {
  createCircleFeature,
  createLineFeature,
  createPolygonFeature,
  getBoundsFromPositions,
  type LatLngTuple,
} from '../../utils/geo';
import type { SiteGeofenceMode } from '../../services/siteService';
import './SiteBuilder.css';

const FALLBACK_VIEW_STATE = {
  latitude: 53.3498,
  longitude: -6.2603,
  zoom: 13,
};

const RADIUS_PRESETS = [100, 250, 500, 1000];
const MIN_POLYGON_VERTICES = 3;

interface SiteBoundaryCanvasProps {
  mode: SiteGeofenceMode;
  onModeChange: (mode: SiteGeofenceMode) => void;
  radiusMetres: string;
  onRadiusChange: (value: string) => void;
  minRadius: number;
  maxRadius: number;
  points: LatLngTuple[];
  onPointsChange: (points: LatLngTuple[]) => void;
  siteCenter: LatLngTuple | null;
  onSiteCenterChange: (center: LatLngTuple) => void;
  siteName?: string;
}

function formatCoordinates([lat, lng]: LatLngTuple): string {
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

function formatArea(squareMetres: number): string {
  if (squareMetres >= 10000) return `${(squareMetres / 10000).toFixed(1)} ha`;
  return `${Math.round(squareMetres)} m²`;
}

export default function SiteBoundaryCanvas({
  mode,
  onModeChange,
  radiusMetres,
  onRadiusChange,
  minRadius,
  maxRadius,
  points,
  onPointsChange,
  siteCenter,
  onSiteCenterChange,
  siteName,
}: SiteBoundaryCanvasProps) {
  useI18n();
  const mapRef = useRef<MapRef | null>(null);
  const vertexIdCounterRef = useRef(0);
  const [vertexIds, setVertexIds] = useState<string[]>([]);
  const [draggingVertexIndex, setDraggingVertexIndex] = useState<number | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [clearConfirmPending, setClearConfirmPending] = useState(false);

  const isPolygon = mode === 'polygon';
  const radiusValue = useMemo(() => {
    const parsed = Number.parseInt(radiusMetres, 10);
    if (!Number.isFinite(parsed)) return 0;
    return Math.min(Math.max(parsed, minRadius), maxRadius);
  }, [maxRadius, minRadius, radiusMetres]);

  const createVertexId = useCallback(() => {
    vertexIdCounterRef.current += 1;
    return `site-boundary-vertex-${vertexIdCounterRef.current}`;
  }, []);

  const polygonFeature = useMemo(
    () => (isPolygon ? createPolygonFeature(points, { kind: 'site-geofence' }) : null),
    [isPolygon, points],
  );
  const openOutlineFeature = useMemo(
    () => (isPolygon && points.length >= 2 ? createLineFeature(points, { kind: 'site-geofence-outline' }) : null),
    [isPolygon, points],
  );
  const circleFeature = useMemo(
    () => (!isPolygon && siteCenter ? createCircleFeature(siteCenter, radiusValue, 96, { kind: 'site-radius' }) : null),
    [isPolygon, radiusValue, siteCenter]);

  const fitPositions = useMemo<LatLngTuple[]>(() => {
    if (isPolygon) return siteCenter ? [siteCenter, ...points] : points;
    if (!siteCenter) return [];
    const outline = createCircleFeature(siteCenter, radiusValue, 24);
    if (!outline) return [siteCenter];
    return outline.geometry.coordinates[0].map(([lng, lat]) => [lat, lng] as LatLngTuple);
  }, [isPolygon, points, radiusValue, siteCenter]);

  const fitToBoundary = useCallback(() => {
    if (!mapRef.current) return;
    const bounds = getBoundsFromPositions(fitPositions);
    if (bounds) {
      mapRef.current.fitBounds(bounds, { padding: 120, duration: 700, maxZoom: 17.5 });
      return;
    }
    if (siteCenter) {
      mapRef.current.flyTo({ center: [siteCenter[1], siteCenter[0]], zoom: 16, duration: 700 });
    }
  }, [fitPositions, siteCenter]);

  // Refit only when the map first loads or the mode changes: refitting on every
  // radius tick or vertex drop would yank the viewport out from under the user.
  const fitRef = useRef(fitToBoundary);
  useEffect(() => { fitRef.current = fitToBoundary; });
  useEffect(() => {
    if (!mapLoaded) return;
    fitRef.current();
  }, [mapLoaded, mode]);

  useEffect(() => {
    setVertexIds((current) => {
      if (current.length === points.length) return current;
      if (current.length < points.length) {
        const next = [...current];
        while (next.length < points.length) next.push(createVertexId());
        return next;
      }
      return current.slice(0, points.length);
    });
  }, [createVertexId, points.length]);

  useEffect(() => {
    if (points.length === 0) setClearConfirmPending(false);
  }, [points.length]);

  const handleMapClick = useCallback((event: { lngLat: { lat: number; lng: number } }) => {
    const position: LatLngTuple = [event.lngLat.lat, event.lngLat.lng];
    if (isPolygon) {
      setVertexIds((current) => [...current, createVertexId()]);
      onPointsChange([...points, position]);
      return;
    }
    if (!siteCenter) onSiteCenterChange(position);
  }, [createVertexId, isPolygon, onPointsChange, onSiteCenterChange, points, siteCenter]);

  const handleVertexDrag = useCallback((vertexIndex: number, event: MarkerDragEvent) => {
    onPointsChange(points.map((point, index) => (
      index === vertexIndex ? [event.lngLat.lat, event.lngLat.lng] : point
    )));
  }, [onPointsChange, points]);

  const handleRemoveVertex = useCallback((vertexIndex: number) => {
    setVertexIds((current) => current.filter((_, index) => index !== vertexIndex));
    onPointsChange(points.filter((_, index) => index !== vertexIndex));
  }, [onPointsChange, points]);

  const handleUndoLast = useCallback(() => {
    setVertexIds((current) => current.slice(0, -1));
    onPointsChange(points.slice(0, -1));
  }, [onPointsChange, points]);

  const handleClear = useCallback(() => {
    setVertexIds([]);
    onPointsChange([]);
    setClearConfirmPending(false);
  }, [onPointsChange]);

  const pointsNeeded = Math.max(MIN_POLYGON_VERTICES - points.length, 0);
  const isReady = isPolygon ? pointsNeeded === 0 : siteCenter != null && radiusValue > 0;
  const circleArea = Math.PI * radiusValue * radiusValue;

  const cursor = isPolygon
    ? (draggingVertexIndex != null ? 'grabbing' : 'crosshair')
    : (siteCenter ? 'grab' : 'crosshair');

  return (
    <div className="sb-canvas">
      <div className="sb-map">
        <MapLibreMap
          ref={mapRef}
          initialViewState={
            siteCenter
              ? { latitude: siteCenter[0], longitude: siteCenter[1], zoom: 16 }
              : FALLBACK_VIEW_STATE
          }
          style={{ width: '100%', height: '100%' }}
          showNavigation={false}
          cursor={cursor}
          onClick={handleMapClick}
          onLoad={() => setMapLoaded(true)}
        >
          {mapLoaded && circleFeature ? (
            <Source id="site-radius-source" type="geojson" data={circleFeature}>
              <Layer id="site-radius-fill" type="fill" paint={{ 'fill-color': '#22c7a8', 'fill-opacity': 0.14 }} />
              <Layer id="site-radius-line" type="line" paint={{ 'line-color': '#22c7a8', 'line-width': 2.5, 'line-opacity': 0.9 }} />
            </Source>
          ) : null}

          {mapLoaded && polygonFeature ? (
            <Source id="site-boundary-polygon" type="geojson" data={polygonFeature}>
              <Layer id="site-boundary-fill" type="fill" paint={{ 'fill-color': '#22c7a8', 'fill-opacity': 0.14 }} />
              <Layer id="site-boundary-line" type="line" paint={{ 'line-color': '#22c7a8', 'line-width': 2.5, 'line-opacity': 0.9 }} />
            </Source>
          ) : null}

          {mapLoaded && !polygonFeature && openOutlineFeature ? (
            <Source id="site-boundary-outline" type="geojson" data={openOutlineFeature}>
              <Layer
                id="site-boundary-outline-line"
                type="line"
                paint={{
                  'line-color': '#22c7a8',
                  'line-width': 2,
                  'line-opacity': 0.75,
                  'line-dasharray': [2, 1.5],
                }}
              />
            </Source>
          ) : null}

          {siteCenter ? (
            <MapPinMarker
              position={siteCenter}
              color={isPolygon ? '#879bb9' : '#1690bf'}
              title={siteName ? t('{name} location', { name: siteName }) : t('Site location')}
              size={isPolygon ? 'sm' : 'lg'}
              draggable={!isPolygon}
              onDragEnd={!isPolygon
                ? (event: MarkerDragEvent) => onSiteCenterChange([event.lngLat.lat, event.lngLat.lng])
                : null}
            />
          ) : null}

          {isPolygon && points.map((point, index) => (
            <MapPinMarker
              key={vertexIds[index] ?? `site-boundary-point-${index}`}
              position={point}
              color={draggingVertexIndex === index ? '#ff8f4d' : '#22c7a8'}
              label={index + 1}
              size="lg"
              title={t('Vertex {number}', { number: index + 1 })}
              draggable
              onDragStart={() => setDraggingVertexIndex(index)}
              onDrag={(event) => handleVertexDrag(index, event)}
              onDragEnd={(event) => {
                handleVertexDrag(index, event);
                setDraggingVertexIndex((current) => (current === index ? null : current));
              }}
              onContextMenu={() => handleRemoveVertex(index)}
            />
          ))}
        </MapLibreMap>
      </div>

      {/* Stats strip -- top centre */}
      <div className="sb-overlay sb-overlay--top">
        <div className="sb-glass sb-stats">
          <div className="sb-stats__cell">
            {t('Mode')} <strong>{isPolygon ? t('Bounds') : t('Radius')}</strong>
          </div>
          {isPolygon ? (
            <>
              <div className={`sb-stats__cell${pointsNeeded === 0 ? ' sb-stats__cell--ok' : ''}`}>
                {t('Vertices')} <strong>{points.length}</strong>
              </div>
              <div className={`sb-stats__cell${pointsNeeded === 0 ? ' sb-stats__cell--ok' : ''}`}>
                {t('Status')}
                <strong>
                  {pointsNeeded === 0
                    ? t('Ready')
                    : tn('{count} more needed', '{count} more needed', pointsNeeded)}
                </strong>
              </div>
            </>
          ) : (
            <>
              <div className={`sb-stats__cell${siteCenter ? ' sb-stats__cell--ok' : ''}`}>
                {t('Size')} <strong>{radiusValue} m</strong>
              </div>
              <div className="sb-stats__cell">
                {t('Covers')} <strong>{formatArea(circleArea)}</strong>
              </div>
            </>
          )}
          <div className="sb-stats__cell">
            {t('Centre')} <strong>{siteCenter ? formatCoordinates(siteCenter) : '—'}</strong>
          </div>
        </div>
      </div>

      {/* Mode switcher -- top left */}
      <div className="sb-overlay sb-overlay--tl">
        <div className="sb-glass sb-modeswitch" role="group" aria-label={t('Site geofence')}>
          <button
            type="button"
            className={`sb-modeswitch__btn${!isPolygon ? ' sb-modeswitch__btn--on' : ''}`}
            aria-pressed={!isPolygon}
            onClick={() => onModeChange('radius')}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="1.5" fill="currentColor" /><path d="M12 12h9" strokeDasharray="2 2" /></svg>
            {t('Radius')}
          </button>
          <button
            type="button"
            className={`sb-modeswitch__btn${isPolygon ? ' sb-modeswitch__btn--on' : ''}`}
            aria-pressed={isPolygon}
            onClick={() => onModeChange('polygon')}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3l8 5.5-3 10.5H7L4 8.5z" /></svg>
            {t('Bounds')}
          </button>
        </div>
        <span className="sb-mode-pill">
          <span className="sb-mode-pill__dot" />
          {isPolygon
            ? t('Click to add a point · drag to move · right-click to delete')
            : siteCenter
              ? t('Drag the pin to move the centre')
              : t('Click the map to drop the site pin')}
        </span>
      </div>

      {/* Zoom + fit -- top right */}
      <div className="sb-overlay sb-overlay--tr">
        <div className="sb-zoom">
          <button type="button" className="sb-zoom__btn" title={t('Zoom in')} aria-label={t('Zoom in')} onClick={() => mapRef.current?.zoomIn()}>+</button>
          <button type="button" className="sb-zoom__btn" title={t('Zoom out')} aria-label={t('Zoom out')} onClick={() => mapRef.current?.zoomOut()}>-</button>
          <button
            type="button"
            className="sb-zoom__btn"
            title={t('Fit to boundary')}
            aria-label={t('Fit to boundary')}
            onClick={fitToBoundary}
            disabled={fitPositions.length === 0}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 9V5a2 2 0 012-2h4M15 3h4a2 2 0 012 2v4M21 15v4a2 2 0 01-2 2h-4M9 21H5a2 2 0 01-2-2v-4" /></svg>
          </button>
        </div>
      </div>

      {/* Legend -- bottom left */}
      <div className="sb-overlay sb-overlay--bl">
        <div className="sb-glass sb-legend">
          <div className="sb-legend__title">{t('Legend')}</div>
          <div className="sb-legend__row">
            <span className="sb-legend__swatch sb-legend__swatch--geo" />
            {isPolygon ? t('Site geofence bounds') : t('Radius geofence')}
          </div>
          <div className="sb-legend__row">
            <span className="sb-legend__swatch sb-legend__swatch--pin" />
            {t('Site location')}
          </div>
          {isPolygon && points.length > 0 && (
            <div className="sb-legend__row">
              <span className="sb-legend__swatch sb-legend__swatch--vertex" />
              {tn('{count} vertex', '{count} vertices', points.length)}
            </div>
          )}
        </div>
      </div>

      {/* Action bar -- bottom centre */}
      <div className="sb-overlay sb-overlay--bc">
        {isPolygon ? (
          <div className="sb-glass sb-actionbar">
            <button type="button" className="sb-actionbar__btn" onClick={handleUndoLast} disabled={points.length === 0}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 7v6h6" /><path d="M3 13a9 9 0 1 0 3-7l-3 3" /></svg>
              {t('Undo')}
            </button>
            {clearConfirmPending ? (
              <div className="sb-actionbar__confirm">
                <span className="sb-actionbar__confirm-label">{t('Clear all points?')}</span>
                <button type="button" className="sb-actionbar__confirm-yes" onClick={handleClear}>{t('Yes, clear')}</button>
                <button type="button" className="sb-actionbar__confirm-no" onClick={() => setClearConfirmPending(false)}>{t('Cancel')}</button>
              </div>
            ) : (
              <button
                type="button"
                className="sb-actionbar__btn"
                onClick={() => setClearConfirmPending(true)}
                disabled={points.length === 0}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 20H7l-4-4 9-9 7 7-5 5" /><line x1="14" y1="6" x2="20" y2="12" /></svg>
                {t('Clear')}
              </button>
            )}
            <div className="sb-actionbar__div" />
            <span className={`sb-actionbar__status${isReady ? ' sb-actionbar__status--ok' : ''}`}>
              {isReady
                ? t('Boundary ready')
                : tn('Add {count} more point', 'Add {count} more points', pointsNeeded)}
            </span>
          </div>
        ) : (
          <div className="sb-glass sb-radiusbar">
            <label className="sb-radiusbar__label" htmlFor="site-radius-slider">{t('Radius')}</label>
            <input
              id="site-radius-slider"
              type="range"
              className="sb-radiusbar__slider"
              min={minRadius}
              max={maxRadius}
              step={5}
              value={radiusValue}
              onChange={(event) => onRadiusChange(event.target.value)}
              disabled={!siteCenter}
            />
            <div className="sb-radiusbar__value">
              <input
                type="number"
                aria-label={t('Radius (meters)')}
                className="sb-radiusbar__input"
                min={minRadius}
                max={maxRadius}
                step={1}
                value={radiusMetres}
                onChange={(event) => onRadiusChange(event.target.value)}
                disabled={!siteCenter}
              />
              <span>m</span>
            </div>
            <div className="sb-actionbar__div" />
            <div className="sb-radiusbar__presets">
              {RADIUS_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={`sb-radiusbar__preset${radiusValue === preset ? ' sb-radiusbar__preset--on' : ''}`}
                  onClick={() => onRadiusChange(String(preset))}
                  disabled={!siteCenter}
                >
                  {preset}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
